import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { ArchwayGarden } from "@/components/landing/ArchwayGarden";
import { useContent } from "@/context/ContentContext";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { scrollY } = useScroll();
  const treeY = useTransform(scrollY, [0, 900], [0, 130]);
  const { content } = useContent();
  const h = content?.hero;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-[#F1F0E8] via-[#F5F4ED] to-[#F7F6F1]"
    >
      <div
        className="absolute inset-0 opacity-[0.04] mix-blend-multiply pointer-events-none"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/34256958/pexels-photo-34256958.jpeg?auto=compress&w=800)",
          backgroundSize: "420px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center pt-28 pb-16">
        <div>
          <motion.p
            custom={0}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            data-testid="hero-eyebrow"
            className="uppercase tracking-[0.35em] text-xs text-[#8C6A1F] mb-8 flex items-center gap-4"
          >
            <span className="w-12 h-px bg-[#A9842F]/70" />
            {h?.eyebrow || "786i.net — Bismillah, we build"}
          </motion.p>

          <motion.h1
            custom={1}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            data-testid="hero-heading"
            className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-medium tracking-tight leading-[0.95] text-[#122A1D]"
          >
            {h?.title || "Sacred Digital"}
            <br />
            <em className="gold-shimmer not-italic">{h?.title_accent || "Craftsmanship"}</em>
          </motion.h1>

          <motion.p
            custom={2}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            data-testid="hero-subtitle"
            className="mt-8 text-base md:text-lg text-[#44584C] max-w-xl font-light leading-relaxed"
          >
            {h?.subtitle ||
              "Web and mobile applications crafted for the Islamic world — where timeless geometry meets living nature, and faith meets flawless engineering."}
          </motion.p>

          <motion.div
            custom={3}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="bg-[#C8A24A] text-[#0B1F16] hover:bg-[#B8923D] font-semibold transition-colors duration-300 px-8 py-4 uppercase tracking-wider text-sm"
            >
              {h?.cta_primary || "Start a Project"}
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="border border-[#A9842F] text-[#8C6A1F] hover:bg-[#C8A24A]/10 transition-colors duration-300 px-8 py-4 uppercase tracking-wider text-sm flex items-center gap-2"
            >
              {h?.cta_secondary || "Explore Services"} <ArrowDown size={16} />
            </a>
          </motion.div>
        </div>

        <motion.div
          style={{ y: treeY }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
          className="hidden lg:flex justify-center"
        >
          <ArchwayGarden />
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#A9842F]/70 animate-bounce z-10">
        <ArrowDown size={20} />
      </div>
    </section>
  );
};
